import { customToast } from "@/components/Common/Toast";
import { getWizardConfig } from "@/config/wizardConfig";
import { useBeneficiaryState } from "@/hooks/useBeneficiaryState";
import { fetchEmployeeData } from "@/services/beneficiaries";
import {
  addBeneficiaryContributionsAPI,
  fetchEmployeeBeneficiaryContributionData,
  fetchEmployeeContributionData,
} from "@/services/contributions";
import { RootState } from "@/store";
import { setShouldResetAllocations } from "@/store/wizard";
import { MinorAccount } from "@/types/beneficiaries";
import {
  APIContribution,
  BeneficiaryContribution,
  BeneficiaryContributionResponse,
} from "@/types/contributions";
import { isBoardingProcessingStatue } from "@/types/employeeState";
import { handleApiError } from "@/utils/errorHandling";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { validateBeneficiaryAllocations } from "../utils/contributionValidation";

interface FundConfig {
  personal: {
    total: number;
    label: string;
  };
  employer: {
    total: number;
    label: string;
  };
}

interface UseBeneficiaryAllocationReturn {
  beneficiaries: BeneficiaryContribution[];
  fundConfig: FundConfig;
  loading: boolean;
  error: string | null;
  isSubmitting: boolean;
  remainingEmployeeAmount: number;
  remainingEmployerAmount: number;
  updatePersonalAllocation: (beneficiaryId: string, amount: number) => void;
  updateEmployerAllocation: (beneficiaryId: string, amount: number) => void;
  splitPersonalEqual: () => void;
  splitEmployerEqual: () => void;
  resetPersonal: () => void;
  resetEmployer: () => void;
  handleSubmitAllocations: () => Promise<boolean>;
  initializeAllocationData: () => Promise<void>;
}

const transformMinorToBeneficiary = (
  minor: MinorAccount,
): BeneficiaryContribution => ({
  beneficiaryId: minor.minorAccountID,
  fullName: [minor.firstName, minor.middleName, minor.lastName]
    .filter(Boolean)
    .join(" "),
  target: 0,
  employeeContributionInDollars: 0,
  employeeContributionInPercent: 0,
  employerContributionInDollars: 0,
  employerContributionInPercent: 0,
  totalContributionPerPaycheck: 0,
  monthlyContribution: 0,
  requiredAmount: 0,
  deficit: 0,
});

export const useBeneficiaryAllocation = (
  isUserEnrolled?: boolean,
): UseBeneficiaryAllocationReturn => {
  const dispatch = useDispatch();
  const {
    id: userAccountID,
    employeeID,
    sootchyEmployerID,
  } = useSelector((state: RootState) => state.user);

  const shouldResetAllocations = useSelector(
    (state: RootState) => state.wizard.shouldResetAllocations,
  );

  const {
    beneficiaries,
    setBeneficiaries,
    fundConfig,
    setFundConfig,
    loading,
    setLoading,
    error,
    setError,
    updatePersonalAllocation,
    updateEmployerAllocation,
    splitPersonalEqual,
    splitEmployerEqual,
    resetPersonal,
    resetEmployer,
    adjustForRoundingErrors,
  } = useBeneficiaryState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { remainingEmployeeAmount, remainingEmployerAmount } = useMemo(() => {
    const totalEmployeeAllocated = Number(
      beneficiaries
        .reduce((sum, b) => sum + b.employeeContributionInDollars, 0)
        .toFixed(2),
    );

    const totalEmployerAllocated = Number(
      beneficiaries
        .reduce((sum, b) => sum + b.employerContributionInDollars, 0)
        .toFixed(2),
    );

    const remainingEmployeeAmount = Number(
      (fundConfig.personal.total - totalEmployeeAllocated).toFixed(2),
    );

    const remainingEmployerAmount = Number(
      (fundConfig.employer.total - totalEmployerAllocated).toFixed(2),
    );

    return {
      remainingEmployeeAmount,
      remainingEmployerAmount,
    };
  }, [beneficiaries, fundConfig]);

  const initializeAllocationData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        employeeContributionResponse,
        beneficiaryContributionResponse,
        beneficiariesResponse,
      ] = await Promise.all([
        fetchEmployeeContributionData(employeeID, userAccountID),
        fetchEmployeeBeneficiaryContributionData(userAccountID, employeeID),
        fetchEmployeeData(),
      ]);

      // Helper function to find existing contribution for a beneficiary
      const findExistingContribution = (
        beneficiary: BeneficiaryContribution,
      ): BeneficiaryContributionResponse | undefined =>
        beneficiaryContributionResponse.data?.find(
          (contrib: BeneficiaryContributionResponse) =>
            contrib.minorAccountID === beneficiary.beneficiaryId,
        );

      // Check for beneficiaries with pending enrollment status only if user is enrolled
      // Only determine reset logic if it hasn't been set yet
      if (shouldResetAllocations === null && isUserEnrolled) {
        const pendingBeneficiaries = beneficiariesResponse.data.filter(
          (beneficiary: MinorAccount) =>
            !isBoardingProcessingStatue(beneficiary.onboardingProgress),
        );

        if (pendingBeneficiaries.length > 0) {
          dispatch(setShouldResetAllocations(true));
        } else {
          dispatch(setShouldResetAllocations(false));
        }
      }

      setFundConfig({
        personal: {
          total:
            employeeContributionResponse.data.employeeTotalContributionInDollar,
          label: "Personal contribution funds",
        },
        employer: {
          total:
            employeeContributionResponse.data.employerTotalContributionInDollar,
          label: "Employer contribution funds",
        },
      });

      const transformedBeneficiaries = beneficiariesResponse.data.map(
        transformMinorToBeneficiary,
      );

      // Check if this is a scenario where we have new beneficiaries mixed with existing ones
      const hasNewBeneficiaries = transformedBeneficiaries.some(
        (beneficiary: BeneficiaryContribution) =>
          !findExistingContribution(beneficiary),
      );

      const hasExistingBeneficiaries = transformedBeneficiaries.some(
        (beneficiary: BeneficiaryContribution) =>
          !!findExistingContribution(beneficiary),
      );

      if (shouldResetAllocations === true) {
        // Reset all allocations if user is enrolled and has pending beneficiaries
        setBeneficiaries(
          transformedBeneficiaries.map((b: BeneficiaryContribution) => ({
            ...b,
            employeeContributionInDollars: 0,
            employeeContributionInPercent: 0,
            employerContributionInDollars: 0,
            employerContributionInPercent: 0,
          })),
        );

        // Set to false immediately after reset to prevent future resets
        dispatch(setShouldResetAllocations(false));
      } else if (
        beneficiaryContributionResponse.data &&
        beneficiaryContributionResponse.data.length > 0 &&
        // Only use existing allocations if we don't have mixed scenario (new + existing beneficiaries)
        !(
          hasNewBeneficiaries &&
          hasExistingBeneficiaries &&
          transformedBeneficiaries.length > 1
        )
      ) {
        // Check for changes in allocation
        const previousEmployeeTotal: number =
          beneficiaryContributionResponse.data.reduce(
            (sum: number, contrib: BeneficiaryContributionResponse) =>
              sum + contrib.employeeMinorContributionInDollar,
            0,
          );
        const previousEmployerTotal: number =
          beneficiaryContributionResponse.data.reduce(
            (sum: number, contrib: BeneficiaryContributionResponse) =>
              sum + contrib.employerMinorContributionInDollar,
            0,
          );

        const currentEmployeeTotal: number =
          employeeContributionResponse.data.employeeTotalContributionInDollar;
        const currentEmployerTotal: number =
          employeeContributionResponse.data.employerTotalContributionInDollar;

        const employeeChanged: boolean =
          Math.abs(currentEmployeeTotal - previousEmployeeTotal) >
          getWizardConfig().contributions.contributionTolerance;
        const employerChanged: boolean =
          Math.abs(currentEmployerTotal - previousEmployerTotal) >
          getWizardConfig().contributions.contributionTolerance;

        if (employeeChanged || employerChanged) {
          // Contributions changed - reset
          setBeneficiaries(
            transformedBeneficiaries.map((b: BeneficiaryContribution) => ({
              ...b,
              employeeContributionInDollars: 0,
              employeeContributionInPercent: 0,
              employerContributionInDollars: 0,
              employerContributionInPercent: 0,
            })),
          );
        } else {
          // Contributions unchanged - use existing allocations
          const populatedBeneficiaries: BeneficiaryContribution[] =
            transformedBeneficiaries.map(
              (beneficiary: BeneficiaryContribution) => {
                const existingContribution =
                  findExistingContribution(beneficiary);

                if (existingContribution) {
                  return {
                    ...beneficiary,
                    employeeContributionInDollars:
                      existingContribution.employeeMinorContributionInDollar,
                    employeeContributionInPercent:
                      existingContribution.employeeMinorContribution,
                    employerContributionInDollars:
                      existingContribution.employerMinorContributionInDollar,
                    employerContributionInPercent:
                      existingContribution.employerMinorContribution,
                  };
                }

                return beneficiary;
              },
            );

          const adjustedForPersonal: BeneficiaryContribution[] =
            adjustForRoundingErrors(
              populatedBeneficiaries,
              employeeContributionResponse.data
                .employeeTotalContributionInDollar,
              "personal",
            );

          const finalBeneficiaries: BeneficiaryContribution[] =
            adjustForRoundingErrors(
              adjustedForPersonal,
              employeeContributionResponse.data
                .employerTotalContributionInDollar,
              "employer",
            );

          setBeneficiaries(finalBeneficiaries);
        }
      } else if (
        hasNewBeneficiaries &&
        hasExistingBeneficiaries &&
        transformedBeneficiaries.length > 1
      ) {
        // Mixed scenario: some beneficiaries have existing allocations, some don't
        // Reset all to 0% to force user to reallocate
        setBeneficiaries(
          transformedBeneficiaries.map((b: BeneficiaryContribution) => ({
            ...b,
            employeeContributionInDollars: 0,
            employeeContributionInPercent: 0,
            employerContributionInDollars: 0,
            employerContributionInPercent: 0,
          })),
        );
      } else {
        // First time user or user enrolled with pending beneficiaries (handled above)
        setBeneficiaries(transformedBeneficiaries);

        // Only auto-allocate if not in reset scenario and only one beneficiary
        if (
          shouldResetAllocations === false &&
          transformedBeneficiaries.length === 1
        ) {
          setTimeout(() => {
            splitPersonalEqual();
            splitEmployerEqual();
          }, 0);
        }
      }
    } catch {
      setError("Failed to load beneficiaries");
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setBeneficiaries,
    setFundConfig,
    setError,
    setLoading,
    splitPersonalEqual,
    splitEmployerEqual,
    employeeID,
    userAccountID,
    adjustForRoundingErrors,
    isUserEnrolled,
    dispatch,
  ]);

  const handleSubmitAllocations = useCallback(async (): Promise<boolean> => {
    if (error) {
      customToast("Unable to continue.", "error");
      return false;
    }

    if (isSubmitting) return false;

    const validation = validateBeneficiaryAllocations(
      fundConfig.personal.total - remainingEmployeeAmount,
      fundConfig.employer.total - remainingEmployerAmount,
      fundConfig.personal.total,
      fundConfig.employer.total,
    );

    if (!validation.isValid) {
      //customToast(validation.errors[0], "error");
      if (
        validation.errors.length > 0 &&
        validation.errors.map((error) => customToast(error, "error"))
      )
        return false;
    }

    try {
      setIsSubmitting(true);

      const apiContributions: APIContribution[] = beneficiaries.map(
        (beneficiaryContribution) => ({
          employeeMinorContribution:
            beneficiaryContribution.employeeContributionInPercent
              ? beneficiaryContribution.employeeContributionInPercent
              : 0,
          employeeMinorContributionInDollar:
            beneficiaryContribution.employeeContributionInDollars,
          employerMinorContribution:
            beneficiaryContribution.employerContributionInPercent
              ? beneficiaryContribution.employerContributionInPercent
              : 0,
          employerMinorContributionInDollar:
            beneficiaryContribution.employerContributionInDollars,
          isEmployeeMinorContributionInPercent: true,
          isEmployerMinorContributionInPercent: true,
          minorAccountID: beneficiaryContribution.beneficiaryId,
          sourceProcess: "Employee Portal",
          changeReason: "Beneficiary allocation update",
        }),
      );

      await addBeneficiaryContributionsAPI(apiContributions, {
        userAccountID,
        employeeID,
        sootchyEmployerID,
      });

      customToast("Your contributions have been successfully set", "success");
      return true;
    } catch (error) {
      handleApiError(error, "Failed to add contributions", true, customToast);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    error,
    isSubmitting,
    remainingEmployeeAmount,
    remainingEmployerAmount,
    fundConfig,
    beneficiaries,
    userAccountID,
    employeeID,
    sootchyEmployerID,
  ]);

  // Handle auto-allocation for single beneficiaries only when appropriate
  useEffect(() => {
    // Only run when data is first loaded (component mount + data loaded)
    if (loading || beneficiaries.length === 0) return;

    // Only auto-allocate for single beneficiary if all allocations are 0
    if (beneficiaries.length === 1) {
      const singleBeneficiary = beneficiaries[0];
      const hasNoAllocation =
        singleBeneficiary.employeeContributionInPercent === 0 &&
        (fundConfig.employer.total === 0 ||
          singleBeneficiary.employerContributionInPercent === 0);

      if (hasNoAllocation) {
        if (fundConfig.personal.total > 0) {
          updatePersonalAllocation(singleBeneficiary.beneficiaryId, 100);
        }

        if (fundConfig.employer.total > 0) {
          updateEmployerAllocation(singleBeneficiary.beneficiaryId, 100);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // ONLY depend on loading state - runs once when data loads

  return {
    beneficiaries,
    fundConfig,
    loading,
    error,
    isSubmitting,
    remainingEmployeeAmount,
    remainingEmployerAmount,
    updatePersonalAllocation,
    updateEmployerAllocation,
    splitPersonalEqual,
    splitEmployerEqual,
    resetPersonal,
    resetEmployer,
    handleSubmitAllocations,
    initializeAllocationData,
  };
};
